/**
 * MUTAGIM.SHOP — Admin Promo Marquee Page
 * Gestion du bandeau promotionnel défilant
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AdminPageWrapper } from '../components/admin/AdminPageWrapper';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Plus,
  Edit2,
  Trash2,
  Loader2,
  Save,
  Sparkles,
  Settings2,
  Eye,
  Megaphone,
  Calendar,
  Link as LinkIcon,
  Mic
} from 'lucide-react';
import { VoiceInput } from '../components/ui/VoiceInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Card,
  CardContent,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Popular emojis for promo messages
const PROMO_ICONS = ['🔥', '⚡', '🎁', '💰', '🚚', '✨', '🏷️', '💎', '🎉', '⭐', '❤️', '🛒'];

export function AdminPromoMarqueePage() {
  const { isLight } = useAdminTheme();
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState({
    is_enabled: true,
    speed: 20,
    background_color: '#111111',
    text_color: '#ffffff',
    separator: '•'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    text: { fr: '', en: '', he: '' },
    link_url: '',
    icon: '🔥',
    highlight_color: '#ef4444',
    is_active: true
  });
  
  const [aiPrompt, setAiPrompt] = useState('');

  const token = localStorage.getItem('admin_token');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/promo-marquee/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch messages');
      
      const data = await res.json();
      setMessages(data.messages || []);
      if (data.settings) {
        setSettings({
          is_enabled: data.settings.is_enabled ?? true,
          speed: data.settings.speed || 20,
          background_color: data.settings.background_color || '#111111',
          text_color: data.settings.text_color || '#ffffff',
          separator: data.settings.separator || '•'
        });
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenCreate = () => {
    setEditingMessage(null);
    setFormData({
      text: { fr: '', en: '', he: '' },
      link_url: '',
      icon: '🔥',
      highlight_color: '#ef4444',
      is_active: true
    });
    setAiPrompt('');
    setEditDialogOpen(true);
  };

  const handleOpenEdit = (message) => {
    setEditingMessage(message);
    setFormData({
      text: message.text || { fr: '', en: '', he: '' },
      link_url: message.link_url || '',
      icon: message.icon || '🔥',
      highlight_color: message.highlight_color || '#ef4444',
      is_active: message.is_active ?? true
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (!formData.text?.fr) {
        toast.error('Le texte français est requis');
        return;
      }

      const endpoint = editingMessage 
        ? `${API_URL}/api/promo-marquee/admin/${editingMessage.id}`
        : `${API_URL}/api/promo-marquee/admin`;
      
      const method = editingMessage ? 'PUT' : 'POST';
      
      const res = await fetch(endpoint, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save message');
      }
      
      toast.success(editingMessage ? 'Message mis à jour' : 'Message ajouté');
      setEditDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!messageToDelete) return;
    
    try {
      setSaving(true);
      
      const res = await fetch(`${API_URL}/api/promo-marquee/admin/${messageToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to delete');
      
      toast.success('Message supprimé');
      setDeleteDialogOpen(false);
      setMessageToDelete(null);
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      
      const res = await fetch(`${API_URL}/api/promo-marquee/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (!res.ok) throw new Error('Failed to save settings');
      
      toast.success('Paramètres enregistrés');
      setSettingsDialogOpen(false);
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (message) => {
    try {
      await fetch(`${API_URL}/api/promo-marquee/admin/${message.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !message.is_active })
      });
      
      toast.success(message.is_active ? 'Message désactivé' : 'Message activé');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleGenerateAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Entrez une description pour générer le message');
      return;
    }
    
    try {
      setGeneratingAI(true);
      toast.info('Génération du message...', { duration: 10000, id: 'gen-promo' });
      
      const res = await fetch(`${API_URL}/api/promo-marquee/admin/generate-ai?prompt=${encodeURIComponent(aiPrompt)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to generate');
      
      const result = await res.json();
      
      if (result.text) {
        setFormData(prev => ({ ...prev, text: result.text }));
        toast.dismiss('gen-promo');
        toast.success('Message généré !');
      }
    } catch (err) {
      toast.dismiss('gen-promo');
      toast.error('Erreur lors de la génération');
    } finally {
      setGeneratingAI(false);
    }
  };

  return (
    <AdminPageWrapper 
      title="Bandeau Promotionnel" 
      subtitle="Gérer les messages promotionnels défilants"
    >
      <div className="space-y-6" data-testid="admin-promo-marquee-page">
        {/* Header Actions */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={settings.is_enabled}
                onCheckedChange={(checked) => {
                  setSettings(prev => ({ ...prev, is_enabled: checked }));
                  fetch(`${API_URL}/api/promo-marquee/admin/settings`, {
                    method: 'PUT',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ ...settings, is_enabled: checked })
                  });
                }}
              />
              <span className={cn("text-sm", isLight ? "text-gray-600" : "text-gray-400")}>
                {settings.is_enabled ? 'Actif' : 'Désactivé'}
              </span>
            </div>
            <span className={cn("text-sm", isLight ? "text-gray-500" : "text-gray-500")}>
              {messages.length} message{messages.length > 1 ? 's' : ''}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setSettingsDialogOpen(true)}
              className={isLight ? "border-gray-300" : "border-zinc-700"}
            >
              <Settings2 className="w-4 h-4 mr-2" />
              Paramètres
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open('/', '_blank')}
              className={isLight ? "border-gray-300" : "border-zinc-700"}
            >
              <Eye className="w-4 h-4 mr-2" />
              Prévisualiser
            </Button>
            <Button
              onClick={handleOpenCreate}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="add-promo-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau message
            </Button>
          </div>
        </div>

        {/* Messages Table */}
        <div className={cn(
          "rounded-xl border overflow-hidden",
          isLight ? "bg-white border-gray-200" : "bg-zinc-900/50 border-zinc-800"
        )}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Megaphone className={cn("w-12 h-12 mb-4", isLight ? "text-gray-300" : "text-gray-600")} />
              <p className={cn("text-lg font-medium mb-2", isLight ? "text-gray-700" : "text-gray-300")}>
                Aucun message promotionnel
              </p>
              <p className={cn("text-sm mb-4", isLight ? "text-gray-500" : "text-gray-500")}>
                Créez des messages accrocheurs pour votre bandeau défilant
              </p>
              <Button onClick={handleOpenCreate} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Créer un message
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={isLight ? "border-gray-200" : "border-zinc-800"}>
                  <TableHead className={cn("w-12", isLight ? "text-gray-600" : "text-gray-400")}></TableHead>
                  <TableHead className={isLight ? "text-gray-600" : "text-gray-400"}>Message</TableHead>
                  <TableHead className={cn("text-center w-24", isLight ? "text-gray-600" : "text-gray-400")}>Lien</TableHead>
                  <TableHead className={cn("text-center w-24", isLight ? "text-gray-600" : "text-gray-400")}>Statut</TableHead>
                  <TableHead className={cn("text-right w-32", isLight ? "text-gray-600" : "text-gray-400")}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {messages.map((msg) => (
                  <TableRow 
                    key={msg.id}
                    className={cn(
                      "transition-colors",
                      !msg.is_active && "opacity-50",
                      isLight ? "border-gray-100 hover:bg-gray-50" : "border-zinc-800 hover:bg-zinc-800/50"
                    )}
                  >
                    <TableCell>
                      <span 
                        className="text-2xl"
                        style={{ color: msg.highlight_color || '#ef4444' }}
                      >
                        {msg.icon || '🔥'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <p className={cn("font-medium", isLight ? "text-gray-900" : "text-white")}>
                        {msg.text?.fr || '—'}
                      </p>
                      {msg.text?.en && (
                        <p className={cn("text-xs mt-1", isLight ? "text-gray-500" : "text-gray-500")}>
                          EN: {msg.text.en}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {msg.link_url ? (
                        <Badge variant="outline" className="text-xs">
                          <LinkIcon className="w-3 h-3 mr-1" />
                          Oui
                        </Badge>
                      ) : (
                        <span className="text-gray-500 text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={msg.is_active}
                        onCheckedChange={() => handleToggleActive(msg)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEdit(msg)}
                          className="h-8 w-8"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setMessageToDelete(msg); setDeleteDialogOpen(true); }}
                          className="h-8 w-8 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className={cn(
          "max-w-lg",
          isLight ? "bg-white" : "bg-zinc-900 border-zinc-800"
        )}>
          <DialogHeader>
            <DialogTitle className={isLight ? "text-gray-900" : "text-white"}>
              {editingMessage ? 'Modifier le message' : 'Nouveau message'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* AI Generation */}
            {!editingMessage && (
              <div className={cn(
                "p-4 rounded-lg border-2 border-dashed",
                isLight ? "border-purple-200 bg-purple-50" : "border-purple-500/30 bg-purple-950/20"
              )}>
                <Label className={cn("text-sm font-medium flex items-center gap-2 mb-2", isLight ? "text-purple-700" : "text-purple-400")}>
                  <Sparkles className="w-4 h-4" />
                  Génération IA
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="Ex: Livraison gratuite, Soldes d'été -50%..."
                    className={cn(
                      "flex-1",
                      isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                    )}
                  />
                  <VoiceInput 
                    onTranscript={(text) => setAiPrompt(prev => prev + ' ' + text)}
                    className={isLight ? "border-gray-300" : "border-zinc-700"}
                  />
                  <Button
                    onClick={handleGenerateAI}
                    disabled={generatingAI}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {generatingAI ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}

            {/* Icon picker */}
            <div className="space-y-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Icône</Label>
              <div className="flex flex-wrap gap-2">
                {PROMO_ICONS.map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, icon }))}
                    className={cn(
                      "w-10 h-10 rounded-lg text-xl flex items-center justify-center transition-all",
                      formData.icon === icon
                        ? "bg-indigo-600 ring-2 ring-indigo-400"
                        : isLight ? "hover:bg-gray-100 bg-gray-50" : "hover:bg-zinc-800 bg-zinc-800/50"
                    )}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>

            {/* Text fields */}
            <div className="space-y-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Message (FR) *</Label>
              <Textarea
                value={formData.text?.fr || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  text: { ...prev.text, fr: e.target.value }
                }))}
                placeholder="Ex: Livraison GRATUITE dès 50€ d'achat !"
                rows={2}
                className={cn(
                  isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Message (EN)</Label>
                <Input
                  value={formData.text?.en || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    text: { ...prev.text, en: e.target.value }
                  }))}
                  placeholder="FREE shipping..."
                  className={cn(
                    isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Message (HE)</Label>
                <Input
                  value={formData.text?.he || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    text: { ...prev.text, he: e.target.value }
                  }))}
                  placeholder="משלוח חינם..."
                  dir="rtl"
                  className={cn(
                    isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                  )}
                />
              </div>
            </div>

            {/* Highlight color */}
            <div className="space-y-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Couleur d'accentuation</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.highlight_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, highlight_color: e.target.value }))}
                  className="w-10 h-10 rounded cursor-pointer"
                />
                <Input
                  value={formData.highlight_color}
                  onChange={(e) => setFormData(prev => ({ ...prev, highlight_color: e.target.value }))}
                  className={cn(
                    "w-32",
                    isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                  )}
                />
              </div>
            </div>

            {/* Link URL */}
            <div className="space-y-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Lien (optionnel)</Label>
              <Input
                value={formData.link_url}
                onChange={(e) => setFormData(prev => ({ ...prev, link_url: e.target.value }))}
                placeholder="https://..."
                className={cn(
                  isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                )}
              />
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between py-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Message actif</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editingMessage ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className={cn(
          "max-w-md",
          isLight ? "bg-white" : "bg-zinc-900 border-zinc-800"
        )}>
          <DialogHeader>
            <DialogTitle className={isLight ? "text-gray-900" : "text-white"}>
              Paramètres du bandeau
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>
                Vitesse: {settings.speed}s
              </Label>
              <Slider
                value={[settings.speed]}
                onValueChange={([value]) => setSettings(prev => ({ ...prev, speed: value }))}
                min={10}
                max={40}
                step={5}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Fond</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.background_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, background_color: e.target.value }))}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.background_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, background_color: e.target.value }))}
                    className={cn(
                      "flex-1",
                      isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                    )}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Texte</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.text_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, text_color: e.target.value }))}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.text_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, text_color: e.target.value }))}
                    className={cn(
                      "flex-1",
                      isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className={isLight ? "text-gray-700" : "text-gray-300"}>Séparateur</Label>
              <Input
                value={settings.separator}
                onChange={(e) => setSettings(prev => ({ ...prev, separator: e.target.value }))}
                placeholder="•"
                className={cn(
                  "w-24",
                  isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                )}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveSettings} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className={isLight ? "bg-white" : "bg-zinc-900 border-zinc-800"}>
          <AlertDialogHeader>
            <AlertDialogTitle className={isLight ? "text-gray-900" : "text-white"}>
              Supprimer le message ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce message promotionnel ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminPageWrapper>
  );
}

export default AdminPromoMarqueePage;
